import L from "leaflet";
import type { ForteXApi, MapObject } from "@fortex/api";
import { catalogueKey, isLeaf, layerId, leavesOf } from "../catalogue/types";
import type { Catalogue, CatalogueEntry } from "../catalogue/types";
import type { Emitter } from "../core/events";
import type { MapCoreEvents } from "../core/api";
import type { HostLayerDefinition, ResolvedOptions } from "../core/options";
import { entryIsOffered } from "../core/options";
import type { PersistedState } from "../core/persisted";
import type { LayerSnapshot, MapStore } from "../core/store";
import { debounce, errorMessage, errorStatus, isAbort } from "../core/support";
import type { FeatureIndex } from "../features/FeatureIndex";
import type { FeatureRenderer } from "../features/FeatureRenderer";
import { entryColor, styleFromEntry } from "../features/style";
import type { Feature, ViewQuery } from "../features/types";
import { format } from "../i18n/labels";
import type { Labels } from "../i18n/labels";
import { hostEntry, hostGroupKey } from "../sources/host";
import { sourceKindOf } from "../sources/registry";
import { entryBounds } from "../sources/tiles";
import type { TrackSource } from "../sources/track";
import type { LayerSource, SourceContext, SourceRegistry, TrackPlayback } from "../sources/types";
import { LayerRegistry, paneNameOf, snapshotOf, zIndexOf } from "./LayerRegistry";
import type { RegistryEntry } from "./LayerRegistry";

export const refreshDelayMs = 200;

export type GroupState = "all" | "some" | "none";

export type LayerManagerOptions = {
  map: L.Map;
  api: ForteXApi;
  store: MapStore;
  options: ResolvedOptions;
  labels: Labels;
  sources: SourceRegistry;
  renderer: FeatureRenderer;
  index: FeatureIndex;
  events: Emitter<MapCoreEvents>;
  persisted: PersistedState;
  savePersisted(patch: Partial<PersistedState>): void;
  initialOn: string[] | null;
  animate: boolean;
};

export function matchesToken(entry: CatalogueEntry, token: string): boolean {
  const lower = token.trim().toLowerCase();

  if (lower.length === 0) return false;

  return (
    lower === entry.key.toLowerCase() ||
    lower === entry.idName.toLowerCase() ||
    lower === `${entry.root}:${entry.key}`.toLowerCase() ||
    lower === layerId(entry.root, entry.key).toLowerCase()
  );
}

export function groupKeyOf(entry: CatalogueEntry): string {
  return `${entry.root}:${entry.key}`;
}

export class LayerManager {
  readonly registry: LayerRegistry;

  private readonly map: L.Map;
  private readonly store: MapStore;
  private readonly options: ResolvedOptions;
  private readonly labels: Labels;
  private readonly sources: SourceRegistry;
  private readonly renderer: FeatureRenderer;
  private readonly index: FeatureIndex;
  private readonly events: Emitter<MapCoreEvents>;
  private readonly settings: LayerManagerOptions;
  private readonly controllers = new Map<string, AbortController>();
  private readonly hostEntries = new Map<string, CatalogueEntry>();
  private readonly hostGroups = new Map<string, string>();
  private readonly trackUnsubscribes = new Map<string, () => void>();
  private readonly scheduled = debounce(refreshDelayMs, () => this.refreshNow());
  private readonly disposers: (() => void)[] = [];
  private catalogue: Catalogue | null = null;
  private initialOn: string[] | null;
  private hostOrder = 0;
  private disposed = false;

  constructor(settings: LayerManagerOptions) {
    this.settings = settings;
    this.map = settings.map;
    this.store = settings.store;
    this.options = settings.options;
    this.labels = settings.labels;
    this.sources = settings.sources;
    this.renderer = settings.renderer;
    this.index = settings.index;
    this.events = settings.events;
    this.initialOn = settings.initialOn;
    this.registry = new LayerRegistry(settings.map);

    this.disposers.push(this.registry.events.on("list", () => this.sync()));
    this.disposers.push(
      this.registry.events.on("change", ({ entry, reason }) => {
        this.sync();
        this.events.emit("layers:change", { entry, reason });

        if (reason === "selected") this.onSelectionChanged(entry);
        if (reason === "opacity") this.onOpacityChanged(entry);
      })
    );
    this.disposers.push(this.registry.events.on("loaded", ({ entries }) => this.events.emit("layers:loaded", { entries })));
    this.disposers.push(this.registry.events.on("error", ({ entry, status, message }) => this.events.emit("layers:error", { entry, status, message })));

    const onMove = () => this.scheduled.call();

    this.map.on("moveend", onMove);
    this.map.on("zoomend", onMove);
    this.disposers.push(() => {
      this.map.off("moveend", onMove);
      this.map.off("zoomend", onMove);
    });

    this.store.setState({ collapsed: [...settings.persisted.collapsed] });
  }

  build(catalogue: Catalogue): void {
    this.catalogue = catalogue;

    if (this.store.getState().collapsed.length === 0 && this.settings.persisted.collapsed.length === 0)
      this.store.setState({ collapsed: catalogue.entries.filter(a => a.kind === "group" && a.collapsed).map(groupKeyOf) });

    const previous = new Map<string, boolean>();

    for (const entry of this.registry.list()) {
      if (entry.entry.origin === "host") continue;

      previous.set(entry.id, entry.selected);
      this.removeEntry(entry.id);
    }

    const leaves = this.offeredLeaves(catalogue);
    const wanted = new Set<string>();

    leaves.forEach((leaf, index) => {
      const source = this.createSource(leaf, index);

      if (source === null) return;

      const selected = previous.get(this.idOf(leaf)) ?? this.initialSelection(leaf);
      const added = this.addSource(leaf, source, selected, index);

      if (added.selected) wanted.add(added.id);
    });

    this.applySingleSelectRules(wanted, previous);
    this.initialOn = null;
    this.registry.emitList();
    this.scheduled.call();
  }

  update(catalogue: Catalogue): void {
    if (this.catalogue === null) {
      this.build(catalogue);
      return;
    }

    const leaves = this.offeredLeaves(catalogue);
    const ids = new Set(leaves.map(a => this.idOf(a)));
    const known = this.offeredLeaves(this.catalogue).map(a => this.idOf(a));

    if (known.length !== ids.size || known.some(a => !ids.has(a))) {
      this.build(catalogue);
      return;
    }

    this.catalogue = catalogue;

    let changed = false;

    for (const leaf of leaves) changed = this.registry.updateEntry(this.idOf(leaf), leaf) || changed;

    if (changed) this.registry.emitList();
  }

  private offeredLeaves(catalogue: Catalogue): CatalogueEntry[] {
    return [...catalogue.maps, ...catalogue.layers].flatMap(leavesOf).filter(a => entryIsOffered(a, this.options));
  }

  catalogueEntries(): CatalogueEntry[] {
    return this.catalogue?.entries ?? [];
  }

  entryOf(id: string): CatalogueEntry | null {
    return this.registry.get(id)?.entry ?? this.hostEntries.get(id) ?? null;
  }

  groupEntry(groupKey: string): CatalogueEntry | null {
    if (this.catalogue === null) return null;

    const direct = this.catalogue.byKey.get(groupKey);

    if (direct) return direct;

    return this.catalogue.entries.find(a => a.key === groupKey || a.idName === groupKey) ?? null;
  }

  list(): LayerSnapshot[] {
    return this.registry.snapshots();
  }

  get(id: string): LayerSnapshot | null {
    const entry = this.resolve(id);

    return entry === null ? null : snapshotOf(entry);
  }

  resolve(id: string): RegistryEntry | null {
    const direct = this.registry.get(id);

    if (direct) return direct;

    return this.registry.list().find(a => matchesToken(a.entry, id)) ?? null;
  }

  setSelected(id: string, on: boolean): void {
    const entry = this.resolve(id);

    if (entry === null) return;

    if (on) this.turnOffSiblingsInSingleSubtree(entry);

    this.registry.setSelected(entry.id, on);
  }

  groupState(groupKey: string): GroupState {
    const group = this.groupEntry(groupKey);
    const leaves = group === null ? [] : this.leafEntries(group);

    if (leaves.length === 0) return "none";

    const on = leaves.filter(a => a.selected).length;

    return on === 0 ? "none" : on === leaves.length ? "all" : "some";
  }

  toggleGroup(groupKey: string, on: boolean): void {
    const group = this.groupEntry(groupKey);

    if (group === null) return;

    const leaves = this.leafEntries(group);

    if (!on) {
      for (const leaf of leaves) this.registry.setSelected(leaf.id, false);
      return;
    }

    const subtrees = new Map<CatalogueEntry, RegistryEntry[]>();

    for (const leaf of leaves) {
      const root = this.singleSubtreeRoot(leaf.entry);

      if (root === null) {
        this.setSelected(leaf.id, true);
        continue;
      }

      const members = subtrees.get(root) ?? [];

      members.push(leaf);
      subtrees.set(root, members);
    }

    for (const members of subtrees.values()) {
      const enabled = members.filter(a => a.disabledReason === null);

      if (enabled.some(a => a.selected)) continue;

      const first = enabled.find(a => a.entry.defaultSelected) ?? enabled[0] ?? null;

      if (first !== null) this.setSelected(first.id, true);
    }
  }

  setCollapsed(groupKey: string, collapsed: boolean): void {
    const current = this.store.getState().collapsed;
    const next = collapsed ? (current.includes(groupKey) ? current : [...current, groupKey]) : current.filter(a => a !== groupKey);

    this.store.setState({ collapsed: next });
    this.settings.savePersisted({ collapsed: next });
  }

  setOpacity(id: string, value: number): void {
    const entry = this.resolve(id);

    if (entry !== null) this.registry.setOpacity(entry.id, value);
  }

  solo(id: string): void {
    const target = this.resolve(id);

    if (target === null) return;

    for (const entry of this.registry.list()) {
      if (entry.entry.root === "maps" && (entry.entry.kind === "tile" || entry.entry.kind === "special")) continue;

      this.registry.setSelected(entry.id, entry.id === target.id);
    }
  }

  fit(id: string): void {
    const entry = this.resolve(id);
    const bounds = entry?.source.bounds() ?? this.renderer.bounds(entry?.id ?? "");

    if (bounds !== null && bounds.isValid()) this.map.fitBounds(bounds, { padding: [48, 48], animate: this.settings.animate });
  }

  allOn(root: "maps" | "layers" | null, on: boolean): void {
    for (const entry of this.registry.list()) {
      if (root !== null && entry.entry.root !== root) continue;
      if (entry.entry.selection === "single") continue;

      this.registry.setSelected(entry.id, on);
    }
  }

  reset(): void {
    if (this.catalogue === null) return;

    this.initialOn = null;

    const wanted = new Set<string>();

    for (const entry of this.registry.list()) {
      if (entry.entry.origin === "host") continue;

      const selected = this.initialSelection(entry.entry);

      this.registry.setSelected(entry.id, selected);

      if (selected) wanted.add(entry.id);
    }

    this.applySingleSelectRules(wanted);
    this.registry.emitList();
  }

  refresh(): void {
    this.scheduled.call();
  }

  addHost(definition: HostLayerDefinition): LayerSnapshot {
    const groupKey = definition.group ?? hostGroupKey;
    const groupName = definition.group ?? this.labels.otherLayers;

    this.hostGroups.set(groupKey, groupName);

    const entry = hostEntry(definition, groupName, this.hostOrder++);
    const id = layerId(entry.settings.hostKind === "track" ? "track" : "custom", entry.key);

    if (this.registry.has(id)) this.removeEntry(id);

    const source = this.createSource(entry, this.hostOrder);

    if (source === null) throw new Error(`host layer ${definition.key} has no source`);

    const snapshot = this.addSource(entry, source, definition.selected ?? true, this.hostOrder);

    this.hostEntries.set(id, entry);

    if (entry.settings.hostKind === "track") void this.loadTrack(id, source as TrackSource);

    return snapshot;
  }

  removeHost(key: string): void {
    const id = [layerId("custom", key), layerId("track", key), key].find(a => this.registry.has(a));

    if (id === undefined) return;

    this.hostEntries.delete(id);
    this.removeEntry(id);
  }

  hostGroupNames(): Map<string, string> {
    return new Map(this.hostGroups);
  }

  features(): Feature[] {
    return this.index.all();
  }

  objects(): MapObject[] {
    return this.index.objects();
  }

  playback(id: string): TrackPlayback | null {
    return this.resolve(id)?.source.playback ?? null;
  }

  selectedKeys(): string[] {
    return this.registry
      .list()
      .filter(a => a.selected && a.entry.origin !== "host")
      .map(a => a.entry.key);
  }

  selectedSources(): LayerSource[] {
    return this.registry
      .list()
      .filter(a => a.selected)
      .map(a => a.source);
  }

  infoSources(): { entry: RegistryEntry; source: LayerSource }[] {
    return this.registry
      .list()
      .filter(a => a.selected && a.source.info !== null)
      .map(a => ({ entry: a, source: a.source }));
  }

  sourceByLayerName(name: string): LayerSource | null {
    return this.registry.list().find(a => a.entry.idName === name || a.entry.key === name || a.id === name)?.source ?? null;
  }

  isDark(): boolean {
    return this.registry.list("maps").some(a => a.selected && ((a.entry.kind === "tile" && a.entry.tile?.dark === true) || (a.entry.kind === "special" && a.entry.special?.dark === true)));
  }

  contentBounds(): L.LatLngBounds | null {
    const bounds = L.latLngBounds([]);

    for (const entry of this.registry.list()) {
      if (!entry.selected || entry.entry.kind === "tile" || entry.entry.kind === "special") continue;

      const extent = entry.source.bounds() ?? this.renderer.bounds(entry.id);

      if (extent !== null && extent.isValid()) bounds.extend(extent);
    }

    if (!bounds.isValid()) {
      for (const feature of this.index.all()) bounds.extend(feature.anchor);
    }

    if (!bounds.isValid()) {
      for (const entry of this.catalogueEntries()) {
        if (entry.kind === "tile" || entry.kind === "special") continue;

        const extent = entryBounds(entry);

        if (extent !== null) bounds.extend(extent);
      }
    }

    return bounds.isValid() ? bounds : null;
  }

  async refreshNow(): Promise<void> {
    if (this.disposed) return;

    const zoom = this.map.getZoom();
    const bounds = this.map.getBounds().pad(0.15);
    const view: ViewQuery = {
      bounds: { minLat: bounds.getSouth(), minLon: bounds.getWest(), maxLat: bounds.getNorth(), maxLon: bounds.getEast() },
      zoom,
      langId: this.store.getState().langId
    };
    const tasks: Promise<void>[] = [];

    for (const entry of this.registry.list()) {
      const provider = entry.source.features;

      if (provider === null) continue;

      if (!entry.selected || !this.registry.inZoomRange(entry, zoom)) {
        this.abort(entry.id);

        if (this.renderer.features(entry.id).length > 0) this.renderer.setFeatures(entry.id, []);

        this.registry.setCount(entry.id, null, null, false);

        if (entry.status !== "idle") this.registry.setStatus(entry.id, "idle");

        continue;
      }

      tasks.push(this.loadEntry(entry, view));
    }

    await Promise.all(tasks);

    if (!this.disposed) this.registry.emitLoaded();
  }

  dispose(): void {
    if (this.disposed) return;

    this.disposed = true;
    this.scheduled.cancel();

    for (const controller of this.controllers.values()) controller.abort();

    this.controllers.clear();

    for (const dispose of this.disposers) dispose();
    for (const unsubscribe of this.trackUnsubscribes.values()) unsubscribe();

    this.trackUnsubscribes.clear();
    this.renderer.dispose();
    this.registry.dispose();
    this.hostEntries.clear();
    this.store.getState().setLayers([]);
  }

  private sync(): void {
    if (this.disposed) return;

    this.store.getState().setLayers(this.registry.snapshots());
  }

  private context(): SourceContext {
    return {
      map: this.map,
      langId: () => this.store.getState().langId,
      authenticated: () => this.settings.api.authenticated,
      admin: () => this.options.info.admin ?? this.settings.api.authenticated,
      paneFor: entry => paneNameOf(entry),
      zIndexFor: entry => zIndexOf(entry, this.indexOf(entry)),
      emitError: (entry, status, message) => {
        const id = this.idOf(entry);
        const registered = this.registry.get(id);

        if (registered !== null) {
          this.registry.setDisabledReason(id, message);
          this.registry.setStatus(id, "error", message, status);
        } else {
          this.events.emit("error", { message, status });
        }
      }
    };
  }

  private indexOf(entry: CatalogueEntry): number {
    if (this.catalogue === null) return 0;

    const siblings = this.catalogue.entries.filter(a => isLeaf(a) && sourceKindOf(a) === sourceKindOf(entry));
    const position = siblings.indexOf(entry);

    return position < 0 ? 0 : position;
  }

  private idOf(entry: CatalogueEntry): string {
    if (entry.origin === "host") return layerId(entry.settings.hostKind === "track" ? "track" : "custom", entry.key);

    return layerId(entry.root, entry.key);
  }

  private createSource(entry: CatalogueEntry, index: number): LayerSource | null {
    const context = this.context();

    context.zIndexFor = target => zIndexOf(target, target === entry ? index : this.indexOf(target));

    try {
      return this.sources.create(entry, context);
    } catch (error) {
      this.events.emit("error", { message: errorMessage(error, this.labels), status: null });

      return null;
    }
  }

  private addSource(entry: CatalogueEntry, source: LayerSource, selected: boolean, index: number): LayerSnapshot {
    const id = this.idOf(entry);
    const root: LayerSnapshot["root"] = entry.origin === "host" ? (entry.settings.hostKind === "track" ? "track" : "custom") : entry.root;
    const disabledReason = source.disabledReason ?? null;
    const registryEntry: RegistryEntry = {
      id,
      key: entry.key,
      root,
      kind: sourceKindOf(entry) ?? entry.kind,
      title: entry.name,
      entry,
      source,
      selected: selected && disabledReason === null,
      opacity: this.settings.persisted.opacities[id] ?? entry.opacity,
      count: entry.source?.counts?.total ?? null,
      countLabel: null,
      status: "idle",
      error: null,
      httpStatus: null,
      minZoom: entry.minZoom,
      maxZoom: entry.maxZoom,
      truncated: false,
      disabledReason,
      zIndex: zIndexOf(entry, index),
      pane: source.pane
    };

    if (source.features !== null) {
      this.registry.ensurePane(source.pane ?? paneNameOf(entry), registryEntry.zIndex);
      this.renderer.addLayer(id, {
        pane: source.pane ?? paneNameOf(entry),
        group: source.layer as L.LayerGroup,
        style: styleFromEntry(entry),
        color: entryColor(entry)
      });
    }

    return this.registry.add(registryEntry);
  }

  private removeEntry(id: string): void {
    this.abort(id);
    this.trackUnsubscribes.get(id)?.();
    this.trackUnsubscribes.delete(id);

    if (this.renderer.hasLayer(id)) this.renderer.removeLayer(id);

    this.registry.remove(id);

    const tracks = { ...this.store.getState().tracks };

    if (id in tracks) {
      delete tracks[id];
      this.store.setState({ tracks });
    }
  }

  private initialSelection(entry: CatalogueEntry): boolean {
    const on = this.initialOn;

    if (on !== null && (entry.root !== "maps" || this.catalogue?.entries.some(a => a.root === "maps" && on.some(token => matchesToken(a, token))))) return on.some(token => matchesToken(entry, token));
    if (this.options.layers.defaultOn.some(token => matchesToken(entry, token))) return true;

    return entry.defaultSelected;
  }

  private leafEntries(group: CatalogueEntry): RegistryEntry[] {
    const keys = new Set(leavesOf(group).map(a => this.idOf(a)));

    return this.registry.list().filter(a => keys.has(a.id));
  }

  private parentOf(entry: CatalogueEntry): CatalogueEntry | null {
    if (entry.parentKey === null || this.catalogue === null) return null;

    return this.catalogue.byKey.get(catalogueKey(entry.root, entry.parentKey)) ?? null;
  }

  private singleSubtreeRoot(entry: CatalogueEntry): CatalogueEntry | null {
    if (entry.selection !== "single") return null;

    let current: CatalogueEntry = entry;
    let parent = this.parentOf(current);

    while (parent !== null && parent.selection === "single") {
      current = parent;
      parent = this.parentOf(current);
    }

    return current === entry ? parent : current;
  }

  private turnOffSiblingsInSingleSubtree(entry: RegistryEntry): void {
    const root = this.singleSubtreeRoot(entry.entry);

    if (root === null) return;

    for (const leaf of this.leafEntries(root)) {
      if (leaf.id !== entry.id) this.registry.setSelected(leaf.id, false);
    }
  }

  private applySingleSelectRules(wanted: Set<string>, previous: ReadonlyMap<string, boolean> = new Map()): void {
    if (this.catalogue === null) return;

    const roots = this.catalogue.entries.filter(a => a.kind === "group" && a.selection === "single" && (this.parentOf(a)?.selection ?? "multi") !== "single");

    for (const root of roots) {
      const leaves = this.leafEntries(root).filter(a => a.disabledReason === null);
      const on = leaves.filter(a => wanted.has(a.id));

      if (on.length === 0) {
        if (leaves.some(a => previous.has(a.id))) continue;

        const first = leaves.find(a => a.entry.defaultSelected) ?? leaves[0] ?? null;

        if (first !== null) this.registry.setSelected(first.id, true);

        continue;
      }

      const kept = on.find(a => previous.get(a.id) === true) ?? on[0];

      for (const leaf of on) {
        if (leaf !== kept) this.registry.setSelected(leaf.id, false);
      }
    }
  }

  private onSelectionChanged(snapshot: LayerSnapshot): void {
    const entry = this.registry.get(snapshot.id);

    if (entry === null) return;

    if (entry.source.features !== null) this.scheduled.call();
    if (entry.entry.origin === "host" && entry.entry.settings.hostKind === "track" && entry.selected && entry.count === null) void this.loadTrack(entry.id, entry.source as TrackSource);
  }

  private onOpacityChanged(snapshot: LayerSnapshot): void {
    const opacities = { ...this.settings.persisted.opacities, [snapshot.id]: snapshot.opacity };

    this.settings.persisted.opacities = opacities;
    this.settings.savePersisted({ opacities });
  }

  private abort(id: string): void {
    this.controllers.get(id)?.abort();
    this.controllers.delete(id);
  }

  private async loadEntry(entry: RegistryEntry, view: ViewQuery): Promise<void> {
    const provider = entry.source.features!;

    this.abort(entry.id);

    const controller = new AbortController();

    this.controllers.set(entry.id, controller);
    this.registry.setStatus(entry.id, "loading");

    try {
      const batch = await provider.load(view, controller.signal);

      if (controller.signal.aborted || this.disposed) return;

      this.renderer.setFeatures(entry.id, batch.features);

      const clusters = batch.features.filter(a => a.cluster !== null).length;
      const total = batch.total > 0 ? batch.total : batch.features.reduce((sum, a) => sum + (a.cluster?.count ?? 1), 0);

      this.registry.setCount(entry.id, total, batch.grouped && clusters > 0 ? `${clusters} ${this.labels.groupsSuffix}` : null, batch.truncated);
      this.registry.setStatus(entry.id, "idle");

      if (batch.truncated) this.store.setState({ hint: { id: `truncated-${entry.id}`, text: format(this.labels.truncatedHint, { count: batch.total, layer: entry.title }) } });
    } catch (error) {
      if (controller.signal.aborted || this.disposed || isAbort(error)) return;

      this.registry.setStatus(entry.id, "error", errorMessage(error, this.labels), errorStatus(error));
    } finally {
      if (this.controllers.get(entry.id) === controller) this.controllers.delete(entry.id);
    }
  }

  private async loadTrack(id: string, source: TrackSource): Promise<void> {
    if (typeof source.load !== "function") return;

    this.registry.setStatus(id, "loading");

    if (source.playback && !this.trackUnsubscribes.has(id)) {
      this.trackUnsubscribes.set(
        id,
        source.playback.subscribe(state => this.store.setState({ tracks: { ...this.store.getState().tracks, [id]: state } }))
      );
    }

    try {
      const vertices = await source.load();

      if (this.disposed) return;

      this.registry.setCount(id, vertices.length);
      this.registry.setStatus(id, "idle");

      if (source.playback) this.store.setState({ tracks: { ...this.store.getState().tracks, [id]: source.playback.state() } });
    } catch (error) {
      if (this.disposed) return;

      this.registry.setStatus(id, "error", errorMessage(error, this.labels), errorStatus(error));
    }
  }
}

export { snapshotOf };
